import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AlertCircle, Loader2, RefreshCw, UserCog } from 'lucide-react';
import { Button } from '@/components/ui-kit/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui-kit/dialog';
import type { OrgRole } from '../../org-permissions/types/permissions.types';

interface DuplicateInvitationDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  email: string;
  existingRole: OrgRole | null;
  newRole: string;
  onResend: () => Promise<void>;
  onUpdateAndResend: () => Promise<void>;
}

export const DuplicateInvitationDialog = ({
  open,
  onOpenChange,
  email,
  existingRole,
  newRole,
  onResend,
  onUpdateAndResend,
}: DuplicateInvitationDialogProps) => {
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState(false);
  const [action, setAction] = useState<'resend' | 'update' | null>(null);

  const getRoleLabel = (role: string | OrgRole | null) => {
    if (!role) return t('ORGTEAM_ROLES_UNKNOWN');
    const roleKey = role.replace('org_', '');
    return t(`ORGTEAM_ROLES_${roleKey.toUpperCase()}`);
  };

  const existingRoleLabel = getRoleLabel(existingRole);
  const newRoleLabel = getRoleLabel(newRole);
  const rolesAreDifferent = existingRole !== `org_${newRole}` && existingRole !== newRole;

  const handleResend = async () => {
    setIsLoading(true);
    setAction('resend');
    try {
      await onResend();
      onOpenChange(false);
    } catch {
      // Error handled in parent
    } finally {
      setIsLoading(false);
      setAction(null);
    }
  };

  const handleUpdateAndResend = async () => {
    setIsLoading(true);
    setAction('update');
    try {
      await onUpdateAndResend();
      onOpenChange(false);
    } catch {
      // Error handled in parent
    } finally {
      setIsLoading(false);
      setAction(null);
    }
  };

  const handleOpenChange = (newOpen: boolean) => {
    if (!isLoading) {
      onOpenChange(newOpen);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="bg-white w-[95vw] max-w-md max-h-[90dvh] p-4 sm:p-6 rounded-2xl flex flex-col gap-4">
        <DialogHeader className="pr-8 text-left">
          <div className="flex items-center gap-3">
            <div className="flex size-10 items-center justify-center rounded-full bg-[hsl(var(--oxford-blue))]/10">
              <AlertCircle className="size-5 text-[hsl(var(--oxford-blue))]" />
            </div>
            <DialogTitle>{t('ORGTEAM_DUPLICATEDIALOG_TITLE')}</DialogTitle>
          </div>
          <DialogDescription className="pt-2">
            {t('ORGTEAM_DUPLICATEDIALOG_DESCRIPTION', {
              email,
              role: existingRoleLabel,
            })}
          </DialogDescription>
        </DialogHeader>

        <div className="py-4">
          <p className="text-sm text-[hsl(var(--globe-grey))]">
            {t('ORGTEAM_DUPLICATEDIALOG_PROMPT')}
          </p>
        </div>

        <DialogFooter className="flex flex-row justify-end gap-2">
          <Button
            onClick={handleResend}
            disabled={isLoading}
            className="w-full"
            variant="outline"
            size="sm"
          >
            {isLoading && action === 'resend' && <Loader2 className="size-4 animate-spin mr-2" />}
            <RefreshCw className="size-4 mr-2" />
            {t('ORGTEAM_DUPLICATEDIALOG_ACTIONS_RESEND')}
          </Button>

          {rolesAreDifferent && (
            <Button onClick={handleUpdateAndResend} disabled={isLoading} className="w-full">
              {isLoading && action === 'update' && <Loader2 className="size-4 animate-spin mr-2" />}
              <UserCog className="size-4 mr-2" />
              {t('ORGTEAM_DUPLICATEDIALOG_ACTIONS_UPDATEANDRESEND', {
                role: newRoleLabel,
              })}
            </Button>
          )}

          <Button
            type="button"
            variant="ghost"
            onClick={() => handleOpenChange(false)}
            disabled={isLoading}
            className="w-full"
            size="sm"
          >
            {t('ORGTEAM_DUPLICATEDIALOG_ACTIONS_CANCEL')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
